import React, { Suspense, lazy } from 'react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

// Lazy loading компонентов - все страницы для code splitting (включая главную, чтобы уменьшить main.js)
export const LazyHomePage = lazy(() => import('../../pages/HomePage/HomePage'));
export const LazyHomePage2 = lazy(() => import('../../pages/HomePage/HomePage2'));
export const LazyMapPage = lazy(() => import('../../pages/MapPage/MapPage'));
export const LazyFeedPage = lazy(() => import('../../pages/FeedPage/FeedPage'));
export const LazyProfilePage = lazy(() => import('../../pages/ProfilePage/ProfilePage'));
export const LazyGroupsPage = lazy(() => import('../../pages/GroupsPage/GroupsPage'));
export const LazyAdminPanel = lazy(() => import('../../pages/AdminPanel/AdminPanel'));
export const LazyRouteDetailPage = lazy(() => import('../../pages/RouteDetailPage/RouteDetailPage'));
export const LazyVideoDetailPage = lazy(() => import('../../pages/VideoDetailPage/VideoDetailPage'));

// Lazy loading для статических страниц
export const LazyLoginPage = lazy(() => import('../../pages/LoginPage/LoginPage'));
export const LazyRegisterPage = lazy(() => import('../../pages/RegisterPage/RegisterPage'));
export const LazyContactUsPage = lazy(() => import('../../pages/ContactUsPage/ContactUsPage'));
export const LazyPrivacyPolicyPage = lazy(() => import('../../pages/PrivacyPolicyPage/PrivacyPolicyPage'));
export const LazyTermsOfServicePage = lazy(() => import('../../pages/TermsOfServicePage/TermsOfServicePage'));
export const LazyCookiePolicyPage = lazy(() => import('../../pages/CookiePolicyPage/CookiePolicyPage'));
export const LazyForgotPasswordPage = lazy(() => import('../../pages/ForgotPasswordPage/ForgotPasswordPage'));
export const LazyResetPasswordPage = lazy(() => import('../../pages/ResetPasswordPage/ResetPasswordPage'));
export const LazyVerifyEmailPage = lazy(() => import('../../pages/VerifyEmailPage/VerifyEmailPage'));
export const LazyAboutPage = lazy(() => import('../../pages/AboutPage/AboutPage'));
export const LazyRouteDrawingGuidePage = lazy(() => import('../../pages/RouteDrawingGuidePage/RouteDrawingGuidePage'));
export const LazyFAQPage = lazy(() => import('../../pages/FAQPage/FAQPage'));
export const LazyForCreatorsPage = lazy(() => import('../../pages/ForCreatorsPage/ForCreatorsPage'));

// Lazy loading компонентов UI
export const LazyMessenger = lazy(() => import('../Messenger/Messenger'));
export const LazyModerationPanel = lazy(() => import('../ModerationPanel/ModerationPanel'));
export const LazyCreateGroupModal = lazy(() => import('../CreateGroupModal/CreateGroupModal'));
export const LazyFriendRequestsManager = lazy(() => import('../FriendRequestsManager/FriendRequestsManager'));

// HOC для ленивой загрузки с Suspense
export const withLazyLoading = (Component, fallback = <LoadingSpinner />) => {
  return (props) => (
    <Suspense fallback={fallback}>
      <Component {...props} />
    </Suspense>
  );
};

// Компонент для предзагрузки ресурсов
export const ResourcePreloader = ({ children, resources = [] }) => {
  React.useEffect(() => {
    // Предзагружаем критические ресурсы
    resources.forEach(resource => {
      if (resource.type === 'image') {
        const img = new Image();
        img.src = resource.url;
      } else if (resource.type === 'script') {
        const script = document.createElement('script');
        script.src = resource.url;
        script.async = true;
        document.head.appendChild(script);
      } else if (resource.type === 'style') {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = resource.url;
        document.head.appendChild(link);
      }
    });
  }, [resources]);

  return children;
};

// Компонент для виртуализации списков
export const VirtualizedList = ({ 
  items, 
  itemHeight, 
  containerHeight, 
  renderItem,
  overscan = 5 
}) => {
  const [scrollTop, setScrollTop] = React.useState(0);
  
  const visibleStart = Math.floor(scrollTop / itemHeight);
  const visibleEnd = Math.min(
    visibleStart + Math.ceil(containerHeight / itemHeight) + overscan,
    items.length
  );
  
  const visibleItems = items.slice(visibleStart, visibleEnd);
  const offsetY = visibleStart * itemHeight;
  const totalHeight = items.length * itemHeight;
  
  return (
    <div
      style={{ height: containerHeight, overflow: 'auto' }}
      onScroll={(e) => setScrollTop(e.target.scrollTop)}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {visibleItems.map((item, index) => 
            renderItem(item, visibleStart + index)
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент для дебаунсинга
export const DebouncedInput = ({ 
  value, 
  onChange, 
  delay = 300, 
  placeholder,
  className,
  ...props 
}) => {
  const [localValue, setLocalValue] = React.useState(value);
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localValue);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [localValue, delay, onChange]);
  
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  return (
    <input
      {...props}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      placeholder={placeholder}
      className={className}
    />
  );
};

// Компонент для мемоизации тяжелых вычислений
export const MemoizedComponent = React.memo(({ data, computeFn, renderFn }) => {
  const computedData = React.useMemo(() => {
    return computeFn(data);
  }, [data, computeFn]);
  
  return renderFn(computedData);
});

// Хук для оптимизации производительности
export const usePerformanceOptimization = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isInViewport, setIsInViewport] = React.useState(false);
  
  const observerRef = React.useRef();
  const elementRef = React.useRef();
  
  // Intersection Observer для ленивой загрузки
  React.useEffect(() => {
    if (!elementRef.current) return;
    
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    
    observerRef.current.observe(elementRef.current);
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  
  // Visibility API для оптимизации при скрытии вкладки
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  return {
    elementRef,
    isVisible,
    isInViewport
  };
};

// Компонент для оптимизации изображений
export const OptimizedImage = ({ 
  src, 
  alt, 
  width, 
  height, 
  className,
  lazy = true,
  placeholder = '/placeholder.jpg'
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isInView, setIsInView] = React.useState(!lazy);
  const imgRef = React.useRef();
  
  React.useEffect(() => {
    if (!lazy) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => observer.disconnect();
  }, [lazy]);
  
  return (
    <div ref={imgRef} className={className} style={{ width, height }}>
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center"
          style={{ width, height }}
        >
          <div className="text-gray-400 text-sm">Загрузка...</div>
        </div>
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          onLoad={() => setIsLoaded(true)}
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}
    </div>
  );
};

export default {
  LazyMapPage,
  LazyFeedPage,
  LazyProfilePage,
  LazyGroupsPage,
  LazyAdminPanel,
  LazyRouteDetailPage,
  LazyVideoDetailPage,
  LazyLoginPage,
  LazyRegisterPage,
  LazyContactUsPage,
  LazyPrivacyPolicyPage,
  LazyTermsOfServicePage,
  LazyCookiePolicyPage,
  LazyForgotPasswordPage,
  LazyResetPasswordPage,
  LazyVerifyEmailPage,
  LazyAboutPage,
  LazyRouteDrawingGuidePage,
  LazyFAQPage,
  LazyForCreatorsPage,
  LazyMessenger,
  LazyModerationPanel,
  LazyCreateGroupModal,
  LazyFriendRequestsManager,
  withLazyLoading,
  ResourcePreloader,
  VirtualizedList,
  DebouncedInput,
  MemoizedComponent,
  usePerformanceOptimization,
  OptimizedImage
};
